import { RevenueObservationService } from './../../../revenue/shared/revenue-observation.service';
import { InvoicePaymentUpsertDto } from './../../dto/invoice-payment-upsert.dto';
import { InvoiceLineItemUpsertDto } from '../../dto/invoice-line-item-upsert.dto';
import { RevenueItemService } from './../../../revenue/shared/revenue-items/revenue-item.service';
import { PrimeNgDropdownEvent } from './../../../shared/primeng/events/dropdown-event';
import { UserSummaryDto } from './../../../users/shared/dto/user-summary.dto';
import { Component, OnInit, Input, Output, EventEmitter, OnChanges, AfterViewInit, SimpleChanges } from '@angular/core';
import { RevenueItem } from 'src/app/revenue/shared/revenue-items/revenue-item.model';
import { TransactionService } from 'src/app/revenue/shared/transaction/transaction.service';
import { TransactionSearchDto } from 'src/app/revenue/shared/transaction/dto/transaction-search.dto';
import { SelectItem } from 'primeng/api';
import { InvoiceUpsertDto } from '../../dto/invoice-upsert.dto';
import { InvoiceUtilityService } from 'src/app/revenue/shared/invoice/invoice-utility/invoice-utility.service';
import { UtilService } from 'src/app/shared';

@Component({
  selector: 'wl-invoice-line-item',
  templateUrl: './invoice-line-item.component.html',
  styleUrls: ['./invoice-line-item.component.less'],
    standalone: false
})
export class InvoiceLineItemComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() invoice: InvoiceUpsertDto;
  @Input() invoiceLineItem: InvoiceLineItemUpsertDto;
  @Input() allRevenueItems: RevenueItem[];
  @Input() allUsers: UserSummaryDto[];
  @Output() removeClicked: EventEmitter<any> = new EventEmitter();

  public showBasePriceField: boolean;
  public showFlatFeeField: boolean;
  public activeRevenueItems: RevenueItem[] = [];
  public debitAccount: string;
  public creditAccount: string;
  public soldBySelectItems: SelectItem[];
  private _cleanInvoiceLineItem: string;

  private _selectedRevenueItem: RevenueItem;

  constructor(
    private _revenueItemService: RevenueItemService,
    private _revenueObservationService: RevenueObservationService,
    private _transactionService: TransactionService,
    private _invoiceUtilityService: InvoiceUtilityService,
    private _utilService: UtilService
  ) {}

  ngOnInit() {
    this.invoiceLineItem.Amount = this.amount;
    this._revenueObservationService.observeRequestInvoiceUpsert().subscribe(() => {
      this.setDirty();
    })
    this._revenueObservationService.observeInvoiceLoaded().subscribe(() => {
      this.setCleanObject();
    });
    this.setCleanObject();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.showBasePriceField = this.invoiceLineItem.BasePrice !== 0;
      this.showFlatFeeField = this.invoiceLineItem.FlatFee !== 0;
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.allRevenueItems && this.invoiceLineItem.ItemNum && !this.selectedRevenueItem) {
      this.selectedRevenueItem = this.allRevenueItems.find((item) => item.ItemNum === this.invoiceLineItem.ItemNum);
    }

    if (!this.activeRevenueItems.length) {
      this.activeRevenueItems = this.allRevenueItems.filter((x) => !x.Inactive || x.ItemNum == this.invoiceLineItem.ItemNum);
    }

    if (this.allUsers) {
      this.soldBySelectItems = this.allUsers.map<SelectItem>((user) => {
        return { label: user.UserName, value: user.UserNum };
      });
    }

    this.setAccounts();
  }

  get dirty() {
    const stripped = this.getStrippedAndCleanedObject();
    return JSON.stringify(stripped) != this._cleanInvoiceLineItem;
  }

  private setDirty() {
    this.invoiceLineItem.Dirty = this.dirty;
  }

  private setCleanObject() {
    const stripped = this.getStrippedAndCleanedObject();
    this._cleanInvoiceLineItem = JSON.stringify(stripped);
  }

  private getStrippedAndCleanedObject() {
    const stripped = this._utilService.clone(this.invoiceLineItem);
    stripped.AdjustmentAmountToApply = stripped.AdjustmentAmountToApply ?? 0;
    stripped.PaymentAmountToApply = stripped.PaymentAmountToApply ?? 0;
    stripped.Payments = [];
    return stripped;
  }

  get amount() {
    return (
      Math.round(
        (this.invoiceLineItem.Quantity * this.invoiceLineItem.UnitPrice + this.invoiceLineItem.BasePrice + this.invoiceLineItem.FlatFee) *
          100
      ) / 100
    );
  }

  get adjustedAmountDue() {
    return this._invoiceUtilityService.calculateLineItemAdjustedAmountDue(this.invoiceLineItem);
  }

  get selectedRevenueItem() {
    return this._selectedRevenueItem;
  }
  set selectedRevenueItem(value: RevenueItem) {
    if (value) {
      this._selectedRevenueItem = value;
      this.invoiceLineItem.Item = value.ItemName;
      this.invoiceLineItem.ItemNum = value.ItemNum;

      if (!this.invoiceLineItem.LineItemId && !this._selectedRevenueItem.TaxType) {
        this.invoiceLineItem.Descr = value.Descr;
        this.invoiceLineItem.UnitPrice = value.UnitPrice;
        this.invoiceLineItem.FlatFee = value.FlatFee;
        this.invoiceLineItem.BasePrice = value.BasePrice;
      }

      this.invoiceAmountChanged();
    }
  }

  invoiceAmountChanged() {
    this.invoiceLineItem.Amount = this.amount;
    this.invoiceLineItem.AdjustedAmountDue = this.adjustedAmountDue;
    this.adjustTaxAmounts();
    this._revenueObservationService.invoiceAmountChanged(this.invoiceLineItem.InvoiceNum);
  }

  onSelectedRevenueItemChange(event: PrimeNgDropdownEvent<RevenueItem>) {
    if (event.value.Taxable) {
      this.getTaxLineItems(event.value);
    } else if (this.invoiceLineItem.TaxLineItems.length > 0) {
      this.invoiceLineItem.TaxLineItems = [];
    }

    if (event.value.DefaultServicePeriodDays === 0) {
      this.invoiceLineItem.ServicePeriod_StartPeriod = this.invoiceLineItem.ServicePeriod_EndPeriod = new Date();
    } else if (event.value.DefaultServicePeriodDays) {
      this.invoiceLineItem.ServicePeriod_StartPeriod = new Date();
      this.invoiceLineItem.ServicePeriod_EndPeriod = new Date();

      if (event.value.DefaultServicePeriodDays === 365) {
        // trunacate one day earlier to prevent auto-generate issues
        this.invoiceLineItem.ServicePeriod_EndPeriod.setDate(this.invoiceLineItem.ServicePeriod_EndPeriod.getDate() + 364);
      } else {
        this.invoiceLineItem.ServicePeriod_EndPeriod.setDate(
          this.invoiceLineItem.ServicePeriod_EndPeriod.getDate() + event.value.DefaultServicePeriodDays
        );
      }
    }
  }

  removeTaxLineItem(index: number) {
    this.invoiceLineItem.TaxLineItems.splice(index, 1);
  }

  private adjustTaxAmounts() {
    if (this.invoiceLineItem.TaxLineItems && this.invoiceLineItem.TaxLineItems.length) {
      this.invoiceLineItem.TaxLineItems.forEach((taxLineItem) => {
        const taxRevenueItem = this.allRevenueItems.find((x) => x.ItemNum === taxLineItem.ItemNum);
        if (taxRevenueItem.TaxFlatAmount > 0) {
          taxLineItem.Amount = taxRevenueItem.TaxFlatAmount;
        } else {
          taxLineItem.Amount = Math.max(taxRevenueItem.TaxPercent * 0.01 * this.invoiceLineItem.Amount, 0);
        }
        if (isNaN(taxLineItem.Amount)) {
          taxLineItem.Amount = 0;
        }
        taxLineItem.Amount = parseFloat(taxLineItem.Amount.toFixed(2));
        taxLineItem.BasePrice = 0;
        taxLineItem.FlatFee = 0;
        taxLineItem.Quantity = 1;
        taxLineItem.UnitPrice = taxLineItem.Amount;
      });
    }
  }

  private getTaxLineItems(taxableRevenueItem: RevenueItem) {
    this._revenueItemService.getTaxLineItems(taxableRevenueItem).then((data) => {
      data.forEach((item) => {
        //item._isTax = true;
        item.ApplyDate = this.invoiceLineItem.ApplyDate;
        item.ServicePeriod_StartPeriod = this.invoiceLineItem.ServicePeriod_StartPeriod;
        item.ServicePeriod_EndPeriod = this.invoiceLineItem.ServicePeriod_EndPeriod;
        item.SoldBy = this.invoiceLineItem.SoldBy;
        item.Payments = new Array<InvoicePaymentUpsertDto>();
        this.invoiceLineItem.TaxLineItems.push(item as InvoiceLineItemUpsertDto);
      });
      this.adjustTaxAmounts();
    });
  }

  private setAccounts() {
    if (this.invoiceLineItem?.Posted) {
      this._transactionService
        .search(new TransactionSearchDto({ LineItemId: this.invoiceLineItem.LineItemId, PageSize: 0 }))
        .then((result) => {
          const creditTransaction = result.Result?.find((x) => x.CreditAmount > 0);
          const debitTransaction = result.Result?.find((x) => x.DebitAmount > 0);
          this.creditAccount = creditTransaction?.Account;
          this.debitAccount = debitTransaction?.Account;
        });
    } else {
      this.creditAccount = this.selectedRevenueItem?.CreditAccount;
      this.debitAccount = this.selectedRevenueItem?.DebitAccount;
    }
  }
}
